import React, { useState, useEffect, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Spin, message, Table, Input, Space, Button } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { ColumnsType, ColumnType } from 'antd/es/table';
import { theme } from '../../theme';
import { 
  PageTitle, 
  StyledCard, 
  SearchableSelect, 
  FormLabel, 
  SearchButton 
} from '../../components/common';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Registrar los componentes de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface Producto {
  id: number;
  tipo: number;
  descripcion: string;
}

interface Persona {
  id: number;
  nc: string;
  denominacion: string;
}

interface SiloBolsa {
  id: number;
  numero_silobolsa: number;
  kilos: number;
  ubicacion: string;
  observacion: string;
  fecha_alta: string;
  fecha_cierre: string;
  estado: string;
  producto: string;
}

interface StockData {
  fecha: string;
  stock: number;
  ingresos: number;
  egresos: number;
  cantidad_ingresos?: number;
  cantidad_egresos?: number;
  detalles_ingresos?: any[];
  detalles_egresos?: any[];
  grupos_ingresos?: any[];
  grupos_egresos?: any[];
}

const EvolucionStock: React.FC = () => {
  const { getAccessTokenSilently, user } = useAuth0();

  // Función para obtener el significado de los tipos de orden
  const getTipoSignificado = (tipo: string): string => {
    const significados: Record<string, string> = {
      'C': 'Compras',
      'V': 'Ventas',
      'A': 'Otra Sucursal',
      'B': 'Baja',
      'E': 'Retiro Cliente',
      'D': 'Dueño->Salida',
      'G': 'Dueño<-Entrada',
      'F': 'Desotra Sucursal'
    };
    return significados[tipo] || tipo;
  };
  const [productos, setProductos] = useState<Producto[]>([]);
  const [clientes, setClientes] = useState<Persona[]>([]);
  const [silosBolsas, setSilosBolsas] = useState<SiloBolsa[]>([]);
  const [selectedProducto, setSelectedProducto] = useState<number>(0);
  const [selectedCliente, setSelectedCliente] = useState<Persona | null>(null);
  const [selectedSiloBolsa, setSelectedSiloBolsa] = useState<number>(0);
  const [selectedUnidad, setSelectedUnidad] = useState<'unidades' | 'kilos'>('kilos');
  const [stockData, setStockData] = useState<StockData[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Estados para búsqueda y filtros
  const [searchTextDetalles, setSearchTextDetalles] = useState<Record<string, string>>({});
  const [searchedColumnDetalles, setSearchedColumnDetalles] = useState<string>('');
  const [searchTextResumen, setSearchTextResumen] = useState<Record<string, string>>({});
  const [searchedColumnResumen, setSearchedColumnResumen] = useState<string>('');

  useEffect(() => {
    const cargarDatos = async () => {
      try {
        const token = await getAccessTokenSilently();
        
        // Cargar productos
        const urlProductos = `${process.env.REACT_APP_BALANZA_URL}/productos/`;
        const responseProductos = await fetch(urlProductos, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (responseProductos.ok) {
          const dataProductos = await responseProductos.json();
          setProductos(dataProductos);
        }

        // Obtener id_empresa del usuario de Auth0
        const id_empresa = user?.id_empresa;
        const id_sucursal = user?.id_sucursal;
        if (!id_empresa || !id_sucursal) {
          console.error('No se encontró el id_empresa o id_sucursal en el usuario');
          return;
        }

        // Cargar clientes (padrón)
        const urlClientes = `${process.env.REACT_APP_PADRON_URL}/leer_padron/${id_empresa}/`;
        const responseClientes = await fetch(urlClientes, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (responseClientes.ok) {
          const dataClientes = await responseClientes.json();
          setClientes(dataClientes);
        }

        // Cargar silos bolsas
        const urlSilosBolsas = `${process.env.REACT_APP_SILOBOLSA_URL}/listado_silobolsa/${id_empresa}/${id_sucursal}/A/`;
        const responseSilosBolsas = await fetch(urlSilosBolsas, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (responseSilosBolsas.ok) {
          const dataSilosBolsas = await responseSilosBolsas.json();
          // Ordenar silos bolsas de mayor a menor por número de silo
          const silosOrdenados = (dataSilosBolsas.silos_bolsas || []).sort((a: SiloBolsa, b: SiloBolsa) => 
            b.numero_silobolsa - a.numero_silobolsa
          );
          setSilosBolsas(silosOrdenados);
        }
      } catch (error) {
        console.error('Error al cargar datos iniciales:', error);
      }
    };

    cargarDatos();
  }, [getAccessTokenSilently, user]);

  // Función para cargar datos de evolución de stock
  const cargarEvolucionStock = async () => {
    if (!selectedProducto || !selectedCliente) {
      return;
    }

    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const id_empresa = user?.id_empresa;
      const id_sucursal = user?.id_sucursal;
      
      if (!id_empresa || !id_sucursal) {
        message.error('No se encontró el id_empresa o id_sucursal del usuario');
        return;
      }

      // Verificar que el producto sea tipo 1, 2 o 3
      const productoSeleccionado = productos.find(p => p.id === selectedProducto);
      if (!productoSeleccionado || (productoSeleccionado.tipo !== 1 && productoSeleccionado.tipo !== 2 && productoSeleccionado.tipo !== 3)) {
        message.warning('Este reporte es solo para productos tipo 1, 2 y 3');
        setLoading(false);
        return;
      }

      // Construir la URL con el filtro de silo bolsa si está seleccionado
      let url = `${process.env.REACT_APP_REPORTES_URL}/evolucion_stock/${id_empresa}/${id_sucursal}/${selectedProducto}/${selectedCliente.id}/`;
      if (selectedSiloBolsa && selectedSiloBolsa !== 0) {
        url += `?silobolsa=${selectedSiloBolsa}`;
      }
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al cargar datos');
      }

      const responseData = await response.json();
      
      if (responseData.status === 'success') {
        // Convertir los datos de la API al formato esperado por el gráfico
        const datosConvertidos: StockData[] = responseData.data.map((item: any) => ({
          fecha: item.fecha,
          stock: item.stock,
          ingresos: item.ingresos,
          egresos: item.egresos,
          cantidad_ingresos: item.cantidad_ingresos,
          cantidad_egresos: item.cantidad_egresos,
          detalles_ingresos: item.detalles_ingresos,
          detalles_egresos: item.detalles_egresos,
          grupos_ingresos: item.grupos_ingresos || [],
          grupos_egresos: item.grupos_egresos || []
        }));
        
        setStockData(datosConvertidos);
        message.success('Datos de evolución de stock cargados');
      } else {
        throw new Error('Error en la respuesta del servidor');
      }
      
    } catch (error) {
      console.error('Error:', error);
      message.error(`Error al cargar los datos de evolución de stock: ${error instanceof Error ? error.message : 'Error desconocido'}`);
    } finally {
      setLoading(false);
    }
  };

  // Removido el useEffect automático - ahora se cargará solo con el botón

  const handleProductoChange = (value: number) => {
    setSelectedProducto(value);
    
    // Si el producto seleccionado es tipo 2, forzar la unidad a "unidades"
    // Si es tipo 3, forzar la unidad a "kilos"
    const productoSeleccionado = productos.find(p => p.id === value);
    if (productoSeleccionado) {
      if (productoSeleccionado.tipo === 2) {
        setSelectedUnidad('unidades');
      } else if (productoSeleccionado.tipo === 3) {
        setSelectedUnidad('kilos');
      }
      
      // Limpiar selección de silo bolsa si el producto no es tipo 3
      if (productoSeleccionado.tipo !== 3) {
        setSelectedSiloBolsa(0);
      }
    }
  };

  const handleUnidadChange = (value: 'unidades' | 'kilos') => {
    setSelectedUnidad(value);
  };

  const handleSiloBolsaChange = (value: number) => {
    setSelectedSiloBolsa(value);
  };

  // Configuración del gráfico
  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: 'Evolución del Stock',
        font: {
          size: 16,
          weight: 'bold'
        }
      },
    },
            scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: selectedUnidad === 'kilos' ? 'Stock (kg)' : 'Stock (Unidades)'
            }
          },
          x: {
            title: {
              display: true,
              text: 'Fecha'
            }
          }
        },
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
  };

  // Datos del gráfico
  const chartData = {
    labels: stockData.map(item => item.fecha),
    datasets: [
      {
        label: selectedUnidad === 'kilos' ? 'Stock (kg)' : 'Stock (Unidades)',
        data: stockData.map((item, index) => {
          if (selectedUnidad === 'kilos') {
            return item.stock;
          } else {
            // Calcular stock acumulado para unidades
            let stockAcumulado = 0;
            for (let i = 0; i <= index; i++) {
              stockAcumulado += (stockData[i].cantidad_ingresos || 0) - (stockData[i].cantidad_egresos || 0);
            }
            return stockAcumulado;
          }
        }),
        borderColor: 'rgb(53, 162, 235)',
        backgroundColor: 'rgba(53, 162, 235, 0.5)',
        tension: 0.1,
        fill: true,
      },
      {
        label: selectedUnidad === 'kilos' ? 'Ingresos (kg)' : 'Ingresos (Unidades)',
        data: stockData.map(item => selectedUnidad === 'kilos' ? item.ingresos : (item.cantidad_ingresos || 0)),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.5)',
        tension: 0.1,
      },
      {
        label: selectedUnidad === 'kilos' ? 'Egresos (kg)' : 'Egresos (Unidades)',
        data: stockData.map(item => selectedUnidad === 'kilos' ? item.egresos : (item.cantidad_egresos || 0)),
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.5)',
        tension: 0.1,
      }
    ],
  };

  // Función para obtener los datos de detalles de movimientos en formato de tabla
  const getDetallesMovimientosData = useMemo(() => {
    const tipoProducto = productos.find(p => p.id === selectedProducto)?.tipo;
    const datos: any[] = [];

    stockData
      .filter(item => (item.cantidad_ingresos || 0) > 0 || (item.cantidad_egresos || 0) > 0)
      .forEach((item) => {
        // Ingresos
        item.detalles_ingresos?.forEach((ingreso: any) => {
          datos.push({
            key: `ingreso-${item.fecha}-${ingreso.numero_ticket || ingreso.produccion_id || ingreso.numero_orden || ingreso.numero_silobolsa || Math.random()}`,
            fecha: new Date(item.fecha).toLocaleDateString('es-AR'),
            fechaRaw: item.fecha,
            tipo: 'INGRESO',
            tipoRaw: 'INGRESO',
            numero1: tipoProducto === 1 ? ingreso.numero_ticket : 
                    tipoProducto === 2 ? (ingreso.produccion_id || ingreso.numero_orden || '-') :
                    tipoProducto === 3 ? (ingreso.numero_silobolsa || ingreso.numero_produccion) : '-',
            numero2: tipoProducto === 1 ? ingreso.numero_ticket :
                    tipoProducto === 2 ? (ingreso.numero_produccion || (ingreso.tipo ? getTipoSignificado(ingreso.tipo) : '-')) :
                    tipoProducto === 3 ? (ingreso.numero_silobolsa || ingreso.numero_produccion) : '-',
            numero3: tipoProducto === 1 ? (ingreso.numero_remito || '-') :
                    tipoProducto === 2 ? (ingreso.numero_orden || '-') :
                    tipoProducto === 3 ? (ingreso.numero_silobolsa || '-') : '-',
            origenDestino: tipoProducto === 2 ? (ingreso.productor || ingreso.destino || '-') : (ingreso.origen || '-'),
            cantidad: selectedUnidad === 'kilos' ? 
                      (ingreso.kilos || ingreso.kilos_netos || ingreso.kilos_semilla || 0) :
                      (ingreso.cantidad || 1),
            cantidadRaw: selectedUnidad === 'kilos' ? 
                         (ingreso.kilos || ingreso.kilos_netos || ingreso.kilos_semilla || 0) :
                         (ingreso.cantidad || 1),
            backgroundColor: theme.colors.successLight
          });
        });

        // Egresos
        item.detalles_egresos?.forEach((egreso: any) => {
          datos.push({
            key: `egreso-${item.fecha}-${egreso.numero_ticket || egreso.numero_orden || egreso.numero_silobolsa || Math.random()}`,
            fecha: new Date(item.fecha).toLocaleDateString('es-AR'),
            fechaRaw: item.fecha,
            tipo: 'EGRESO',
            tipoRaw: 'EGRESO',
            numero1: tipoProducto === 1 ? egreso.numero_ticket :
                    tipoProducto === 2 ? (egreso.numero_orden || '-') :
                    tipoProducto === 3 ? egreso.numero_silobolsa : '-',
            numero2: tipoProducto === 1 ? egreso.numero_produccion :
                    tipoProducto === 2 ? (egreso.tipo_destino ? getTipoSignificado(egreso.tipo_destino) : '-') :
                    tipoProducto === 3 ? '-' : '-',
            numero3: tipoProducto === 1 ? '-' :
                    tipoProducto === 2 ? (egreso.numero_orden || '-') :
                    tipoProducto === 3 ? (egreso.numero_silobolsa || '-') : '-',
            origenDestino: egreso.destino || '-',
            cantidad: selectedUnidad === 'kilos' ?
                      (egreso.kilos || egreso.kilos_salida || egreso.carga_en_kilos || 0) :
                      (egreso.cantidad || 1),
            cantidadRaw: selectedUnidad === 'kilos' ?
                         (egreso.kilos || egreso.kilos_salida || egreso.carga_en_kilos || 0) :
                         (egreso.cantidad || 1),
            backgroundColor: theme.colors.errorLight
          });
        });
      });

    return datos;
  }, [stockData, selectedProducto, selectedUnidad, productos, getTipoSignificado]);

  // Función para obtener los datos de resumen agrupado en formato de tabla
  const getResumenAgrupadoData = useMemo(() => {
    const tipoProducto = productos.find(p => p.id === selectedProducto)?.tipo;
    const datos: any[] = [];

    stockData
      .filter(item => (item.grupos_ingresos && item.grupos_ingresos.length > 0) || (item.grupos_egresos && item.grupos_egresos.length > 0))
      .forEach((item) => {
        // Grupos de ingresos
        item.grupos_ingresos?.forEach((grupo: any) => {
          let referencia = '';
          if (tipoProducto === 1) referencia = `Ticket: ${grupo.numero_ticket}`;
          else if (tipoProducto === 2) {
            if (grupo.numero_produccion) referencia = `Prod: ${grupo.numero_produccion} - ${grupo.productor}`;
            else if (grupo.numero_orden) referencia = `Orden: ${grupo.numero_orden} (${getTipoSignificado(grupo.tipo)}) - ${grupo.origen}`;
            else referencia = 'N/A';
          }
          else if (tipoProducto === 3) referencia = `Prod: ${grupo.numero_produccion}`;
          else referencia = 'N/A';

          datos.push({
            key: `ingreso-grupo-${item.fecha}-${grupo.numero_ticket || grupo.numero_produccion || grupo.numero_orden || Math.random()}`,
            fecha: new Date(item.fecha).toLocaleDateString('es-AR'),
            fechaRaw: item.fecha,
            tipo: 'INGRESO',
            tipoRaw: 'INGRESO',
            referencia,
            cantidad: tipoProducto === 2 ? (grupo.kilos || 0) : grupo.cantidad,
            cantidadRaw: tipoProducto === 2 ? (grupo.kilos || 0) : grupo.cantidad,
            unidades: tipoProducto === 2 ? grupo.cantidad : 
                     (selectedUnidad === 'kilos' ? (grupo.kilos || 0) : grupo.cantidad),
            unidadesRaw: tipoProducto === 2 ? grupo.cantidad :
                         (selectedUnidad === 'kilos' ? (grupo.kilos || 0) : grupo.cantidad),
            backgroundColor: theme.colors.successLight
          });
        });

        // Grupos de egresos
        item.grupos_egresos?.forEach((grupo: any) => {
          let referencia = '';
          if (tipoProducto === 1) referencia = `Remito: ${grupo.numero_remito}`;
          else if (tipoProducto === 2) referencia = `Orden: ${grupo.numero_orden} (${getTipoSignificado(grupo.tipo)}) - ${grupo.origen}`;
          else if (tipoProducto === 3) referencia = `Silo: ${grupo.numero_orden}`;
          else referencia = 'N/A';

          datos.push({
            key: `egreso-grupo-${item.fecha}-${grupo.numero_remito || grupo.numero_orden || Math.random()}`,
            fecha: new Date(item.fecha).toLocaleDateString('es-AR'),
            fechaRaw: item.fecha,
            tipo: 'EGRESO',
            tipoRaw: 'EGRESO',
            referencia,
            cantidad: tipoProducto === 2 ? (grupo.kilos || 0) : grupo.cantidad,
            cantidadRaw: tipoProducto === 2 ? (grupo.kilos || 0) : grupo.cantidad,
            unidades: tipoProducto === 2 ? grupo.cantidad :
                     (selectedUnidad === 'kilos' ? (grupo.kilos || 0) : grupo.cantidad),
            unidadesRaw: tipoProducto === 2 ? grupo.cantidad :
                         (selectedUnidad === 'kilos' ? (grupo.kilos || 0) : grupo.cantidad),
            backgroundColor: theme.colors.errorLight
          });
        });
      });

    return datos;
  }, [stockData, selectedProducto, selectedUnidad, productos, getTipoSignificado]);

  // Función para obtener propiedades de búsqueda en columnas
  const getColumnSearchProps = (dataIndex: string, placeholder: string, isDetalles: boolean = true) => {
    const searchState = isDetalles ? searchTextDetalles : searchTextResumen;
    const setSearchState = isDetalles ? setSearchTextDetalles : setSearchTextResumen;
    const searchedColumn = isDetalles ? searchedColumnDetalles : searchedColumnResumen;
    const setSearchedColumn = isDetalles ? setSearchedColumnDetalles : setSearchedColumnResumen;

    return {
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder={placeholder}
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => {
              setSearchState({ ...searchState, [dataIndex]: selectedKeys[0] });
              setSearchedColumn(dataIndex);
              confirm();
            }}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => {
                setSearchState({ ...searchState, [dataIndex]: selectedKeys[0] });
                setSearchedColumn(dataIndex);
                confirm();
              }}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Buscar
            </Button>
            <Button
              onClick={() => {
                clearFilters?.();
                const newSearchState = { ...searchState };
                delete newSearchState[dataIndex];
                setSearchState(newSearchState);
                setSearchedColumn('');
              }}
              size="small"
              style={{ width: 90 }}
            >
              Limpiar
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? theme.colors.primary : undefined }} />
      ),
      onFilter: (value: any, record: any) => {
        const fieldValue = record[dataIndex];
        if (fieldValue === null || fieldValue === undefined) return false;
        return fieldValue.toString().toLowerCase().includes(value.toLowerCase());
      },
    };
  };

  // Columnas para Detalles de Movimientos
  const columnasDetalles: ColumnsType<any> = useMemo(() => {
    const tipoProducto = productos.find(p => p.id === selectedProducto)?.tipo;
    
    return [
      {
        title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}>Fecha</span>,
        dataIndex: 'fecha',
        key: 'fecha',
        width: 110,
        sorter: (a, b) => new Date(a.fechaRaw).getTime() - new Date(b.fechaRaw).getTime(),
        defaultSortOrder: 'descend' as const,
        ...getColumnSearchProps('fecha', 'Buscar por fecha...', true),
        render: (text: string) => (
          <span style={{ color: theme.colors.textPrimary, fontWeight: 500 }}>{text}</span>
        ),
      },
      {
        title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}>Tipo</span>,
        dataIndex: 'tipo',
        key: 'tipo',
        width: 100,
        filters: [
          { text: 'INGRESO', value: 'INGRESO' },
          { text: 'EGRESO', value: 'EGRESO' },
        ],
        onFilter: (value: any, record: any) => record.tipoRaw === value,
        render: (text: string) => (
          <span style={{
            display: 'inline-block',
            padding: '4px 12px',
            borderRadius: '9999px',
            fontSize: '12px',
            fontWeight: 600,
            backgroundColor: text === 'INGRESO' ? theme.colors.successLight : theme.colors.errorLight,
            color: text === 'INGRESO' ? theme.colors.successDark : theme.colors.errorDark
          }}>
            {text}
          </span>
        ),
      },
      {
        title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}>N° {tipoProducto === 1 ? 'Ticket' : tipoProducto === 2 ? 'Producción/Orden' : tipoProducto === 3 ? 'Producción' : 'Item'}</span>,
        dataIndex: 'numero1',
        key: 'numero1',
        width: tipoProducto === 2 ? 160 : 120,
        sorter: (a, b) => {
          const aVal = a.numero1?.toString() || '';
          const bVal = b.numero1?.toString() || '';
          return aVal.localeCompare(bVal);
        },
        ...getColumnSearchProps('numero1', 'Buscar número...', true),
        render: (text: string) => (
          <span style={{ color: theme.colors.textPrimary, fontWeight: 500 }}>{text || '-'}</span>
        ),
      },
      {
        title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}>N° {tipoProducto === 1 ? 'Producción' : tipoProducto === 2 ? 'Producción/Tipo' : tipoProducto === 3 ? 'Producción' : 'Secundario'}</span>,
        dataIndex: 'numero2',
        key: 'numero2',
        width: tipoProducto === 2 ? 160 : 130,
        sorter: (a, b) => {
          const aVal = a.numero2?.toString() || '';
          const bVal = b.numero2?.toString() || '';
          return aVal.localeCompare(bVal);
        },
        ...getColumnSearchProps('numero2', 'Buscar número...', true),
        render: (text: string) => (
          <span style={{ color: theme.colors.textPrimary, fontWeight: 500 }}>{text || '-'}</span>
        ),
      },
      {
        title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}>N° {tipoProducto === 1 ? 'Remito' : tipoProducto === 2 ? 'Orden' : tipoProducto === 3 ? 'Silo Bolsa' : 'Referencia'}</span>,
        dataIndex: 'numero3',
        key: 'numero3',
        width: tipoProducto === 3 ? 140 : 120,
        sorter: (a, b) => {
          const aVal = a.numero3?.toString() || '';
          const bVal = b.numero3?.toString() || '';
          return aVal.localeCompare(bVal);
        },
        ...getColumnSearchProps('numero3', 'Buscar número...', true),
        render: (text: string) => (
          <span style={{ color: theme.colors.textPrimary, fontWeight: 500 }}>{text || '-'}</span>
        ),
      },
      {
        title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}>Origen/Destino</span>,
        dataIndex: 'origenDestino',
        key: 'origenDestino',
        width: 180,
        sorter: (a, b) => {
          const aVal = a.origenDestino?.toString() || '';
          const bVal = b.origenDestino?.toString() || '';
          return aVal.localeCompare(bVal);
        },
        ...getColumnSearchProps('origenDestino', 'Buscar origen/destino...', true),
        render: (text: string) => (
          <span style={{ color: theme.colors.textSecondary }}>{text || '-'}</span>
        ),
      },
      {
        title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}>{selectedUnidad === 'kilos' ? 'Kilos' : 'Unidades'}</span>,
        dataIndex: 'cantidad',
        key: 'cantidad',
        width: 100,
        align: 'right' as const,
        sorter: (a, b) => a.cantidadRaw - b.cantidadRaw,
        render: (text: number) => (
          <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>
            {selectedUnidad === 'kilos' ? text.toFixed(2) : text}
          </span>
        ),
      },
    ];
  }, [selectedProducto, selectedUnidad, productos, searchTextDetalles, searchedColumnDetalles]);

  // Columnas para Resumen Agrupado
  const columnasResumen: ColumnsType<any> = useMemo(() => {
    const tipoProducto = productos.find(p => p.id === selectedProducto)?.tipo;
    
    return [
      {
        title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Fecha</span>,
        dataIndex: 'fecha',
        key: 'fecha',
        sorter: (a, b) => new Date(a.fechaRaw).getTime() - new Date(b.fechaRaw).getTime(),
        defaultSortOrder: 'descend' as const,
        ...getColumnSearchProps('fecha', 'Buscar por fecha...', false),
        render: (text: string) => (
          <span style={{ color: theme.colors.textPrimary, fontWeight: 500 }}>{text}</span>
        ),
      },
      {
        title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Tipo</span>,
        dataIndex: 'tipo',
        key: 'tipo',
        filters: [
          { text: 'INGRESO', value: 'INGRESO' },
          { text: 'EGRESO', value: 'EGRESO' },
        ],
        onFilter: (value: any, record: any) => record.tipoRaw === value,
        render: (text: string) => (
          <span style={{
            display: 'inline-block',
            padding: '4px 12px',
            borderRadius: '9999px',
            fontSize: '12px',
            fontWeight: 600,
            backgroundColor: text === 'INGRESO' ? theme.colors.successLight : theme.colors.errorLight,
            color: text === 'INGRESO' ? theme.colors.successDark : theme.colors.errorDark
          }}>
            {text}
          </span>
        ),
      },
      {
        title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>{tipoProducto === 1 ? 'N° Ticket/Remito' : tipoProducto === 2 ? 'N° Producción/Orden' : tipoProducto === 3 ? 'N° Producción/Silo' : 'N° Referencia'}</span>,
        dataIndex: 'referencia',
        key: 'referencia',
        sorter: (a, b) => {
          const aVal = a.referencia?.toString() || '';
          const bVal = b.referencia?.toString() || '';
          return aVal.localeCompare(bVal);
        },
        ...getColumnSearchProps('referencia', 'Buscar referencia...', false),
        render: (text: string) => (
          <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>{text}</span>
        ),
      },
      {
        title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Cantidad</span>,
        dataIndex: 'cantidad',
        key: 'cantidad',
        align: 'right' as const,
        sorter: (a, b) => a.cantidadRaw - b.cantidadRaw,
        render: (text: number) => (
          <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>
            {tipoProducto === 2 ? text.toFixed(2) : text}
          </span>
        ),
      },
      {
        title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>{tipoProducto === 2 ? 'Unidades' : (selectedUnidad === 'kilos' ? 'Kilos' : 'Unidades')}</span>,
        dataIndex: 'unidades',
        key: 'unidades',
        align: 'right' as const,
        sorter: (a, b) => a.unidadesRaw - b.unidadesRaw,
        render: (text: number) => (
          <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>
            {tipoProducto === 2 ? text : (selectedUnidad === 'kilos' ? text.toFixed(2) : text)}
          </span>
        ),
      },
    ];
  }, [selectedProducto, selectedUnidad, productos, searchTextResumen, searchedColumnResumen]);

  return (
    <div style={{ padding: '0' }}>
      <PageTitle>Evolución de Stock</PageTitle>

      {/* Card de filtros */}
      <StyledCard 
        title="Filtros de Búsqueda"
        style={{ 
          overflow: 'hidden', 
          maxWidth: '100%',
          marginBottom: '16px'
        }}
        bodyStyle={{ 
          overflow: 'hidden', 
          overflowX: 'hidden', 
          maxWidth: '100%',
          padding: '12px 16px'
        }}
        headStyle={{
          padding: '8px 16px',
          fontSize: '14px',
          fontWeight: 600
        }}
      >
        <div style={{ 
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden'
        }}>
        
        {/* Primera fila: Producto, Cliente, Unidades, Silo Bolsa, Buscar */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '12px',
          alignItems: 'end',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          minWidth: 0,
          overflow: 'hidden'
        }}>
          
          {/* Select de Productos */}
          <div style={{ minWidth: 0, width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
            <FormLabel style={{ fontSize: '12px', marginBottom: '4px' }}>Producto:</FormLabel>
            <SearchableSelect
              placeholder="Seleccione un producto"
              value={selectedProducto || undefined}
              onChange={handleProductoChange}
              options={productos.map(producto => ({
                value: producto.id,
                label: producto.descripcion
              }))}
            />
          </div>

          {/* Select de Clientes con búsqueda */}
          <div style={{ minWidth: 0, width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
            <FormLabel style={{ fontSize: '12px', marginBottom: '4px' }}>Cliente:</FormLabel>
            <SearchableSelect
              placeholder="Buscar por N° Cliente o Razón Social"
              value={selectedCliente ? selectedCliente.id : undefined}
              onChange={(value) => {
                const cliente = clientes.find(c => c.id === value);
                setSelectedCliente(cliente || null);
              }}
              options={clientes.map(cliente => ({
                value: cliente.id,
                label: `${cliente.nc} - ${cliente.denominacion}`
              }))}
            />
          </div>

          {/* Select de Unidades */}
          <div style={{ minWidth: 0, width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
            <FormLabel style={{ fontSize: '12px', marginBottom: '4px' }}>Unidades:</FormLabel>
            <SearchableSelect
              placeholder="Seleccione unidad"
              value={selectedUnidad}
              onChange={handleUnidadChange}
              disabled={selectedProducto ? (productos.find(p => p.id === selectedProducto)?.tipo === 3) : false}
              options={[
                ...(productos.find(p => p.id === selectedProducto)?.tipo !== 3 ? [{ value: 'kilos', label: 'Kilos' }] : []),
                ...(productos.find(p => p.id === selectedProducto)?.tipo !== 3 ? [{ value: 'unidades', label: 'Unidades' }] : [])
              ]}
            />
          </div>

          {/* Select de Silo Bolsas */}
          <div style={{ minWidth: 0, width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
            <FormLabel style={{ fontSize: '12px', marginBottom: '4px' }}>Silo Bolsa:</FormLabel>
            <SearchableSelect
              placeholder={productos.find(p => p.id === selectedProducto)?.tipo === 3 ? "Seleccione un Silo/Bolsa" : "Solo disponible para productos tipo 3 (Semilla)"}
              value={selectedSiloBolsa || undefined}
              onChange={handleSiloBolsaChange}
              options={[
                { value: 0, label: 'Todos los Silo/Bolsas' },
                ...silosBolsas.map(silo => ({
                  value: silo.id,
                  label: `${silo.numero_silobolsa} - ${silo.ubicacion} (${silo.kilos.toFixed(2)} kg)`
                }))
              ]}
              disabled={!selectedProducto || !selectedCliente || (productos.find(p => p.id === selectedProducto)?.tipo !== 3)}
            />
          </div>

          {/* Botón Buscar */}
          <div style={{ minWidth: 0, width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
            <FormLabel style={{ color: 'transparent', fontSize: '12px', marginBottom: '4px' }}>&nbsp;</FormLabel>
            <SearchButton
              onClick={cargarEvolucionStock}
              disabled={!selectedProducto || !selectedCliente || loading}
              loading={loading}
              style={{ width: '100%', height: '32px', fontSize: '13px' }}
            />
          </div>
        </div>
        </div>
      </StyledCard>

      {/* Gráfico de Chart.js */}
      {loading ? (
        <StyledCard darkHeader={false} style={{ marginBottom: '16px' }}>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
            <p style={{ marginTop: '16px', color: theme.colors.textSecondary, fontSize: '14px' }}>Cargando datos de evolución de stock...</p>
          </div>
        </StyledCard>
      ) : selectedProducto && selectedCliente ? (
        <div>
          <StyledCard 
            title={
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '6px' }}>
                  Evolución de Stock - {productos.find(p => p.id === selectedProducto)?.descripcion}
                </div>
                <div style={{ fontSize: '13px', fontWeight: 400, color: theme.colors.textSecondary }}>
                  Cliente: {selectedCliente.nc} - {selectedCliente.denominacion}
                  {selectedSiloBolsa && selectedSiloBolsa !== 0 ? ` - Silo/Bolsa: ${silosBolsas.find(s => s.id === selectedSiloBolsa)?.numero_silobolsa}` : ''}
                </div>
              </div>
            }
            style={{ marginBottom: '16px' }}
            headStyle={{
              padding: '8px 16px',
              fontSize: '14px'
            }}
            bodyStyle={{ padding: '12px' }}
          >
            <div style={{ height: '350px', position: 'relative', padding: '12px 0' }}>
              <Line options={chartOptions} data={chartData} />
            </div>
          </StyledCard>
            
          {/* Resumen de datos */}
          <StyledCard 
            title="Resumen de Movimientos"
            style={{ marginBottom: '16px' }}
            headStyle={{
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: 600
            }}
            bodyStyle={{ padding: '12px' }}
          >
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '12px'
            }}>
              <div style={{ textAlign: 'center', padding: '16px', backgroundColor: theme.colors.infoLight, borderRadius: theme.borderRadius.md }}>
                <h4 style={{ color: theme.colors.textSecondary, marginBottom: '8px', fontSize: '12px', fontWeight: 500 }}>Stock Actual</h4>
                <p style={{ fontSize: '24px', fontWeight: 'bold', color: theme.colors.primary, margin: 0 }}>
                  {stockData.length > 0 
                    ? selectedUnidad === 'kilos' 
                      ? new Intl.NumberFormat('es-AR').format(stockData[stockData.length - 1].stock)
                      : new Intl.NumberFormat('es-AR').format(stockData.reduce((sum, item) => sum + (item.cantidad_ingresos || 0) - (item.cantidad_egresos || 0), 0))
                    : 0}
                </p>
                <p style={{ fontSize: '11px', color: theme.colors.textSecondary, marginTop: '4px' }}>
                  {selectedUnidad === 'kilos' ? 'kg' : 'unidades'}
                </p>
              </div>
              <div style={{ textAlign: 'center', padding: '16px', backgroundColor: theme.colors.successLight, borderRadius: theme.borderRadius.md }}>
                <h4 style={{ color: theme.colors.textSecondary, marginBottom: '8px', fontSize: '12px', fontWeight: 500 }}>Total Ingresos</h4>
                <p style={{ fontSize: '24px', fontWeight: 'bold', color: theme.colors.success, margin: 0 }}>
                  {selectedUnidad === 'kilos' 
                    ? new Intl.NumberFormat('es-AR').format(stockData.reduce((sum, item) => sum + item.ingresos, 0))
                    : new Intl.NumberFormat('es-AR').format(stockData.reduce((sum, item) => sum + (item.cantidad_ingresos || 0), 0))
                  }
                </p>
                <p style={{ fontSize: '11px', color: theme.colors.textSecondary, marginTop: '4px' }}>
                  {selectedUnidad === 'kilos' ? 'kg' : 'unidades'}
                </p>
              </div>
              <div style={{ textAlign: 'center', padding: '16px', backgroundColor: theme.colors.errorLight, borderRadius: theme.borderRadius.md }}>
                <h4 style={{ color: theme.colors.textSecondary, marginBottom: '8px', fontSize: '12px', fontWeight: 500 }}>Total Egresos</h4>
                <p style={{ fontSize: '24px', fontWeight: 'bold', color: theme.colors.error, margin: 0 }}>
                  {selectedUnidad === 'kilos' 
                    ? new Intl.NumberFormat('es-AR').format(stockData.reduce((sum, item) => sum + item.egresos, 0))
                    : new Intl.NumberFormat('es-AR').format(stockData.reduce((sum, item) => sum + (item.cantidad_egresos || 0), 0))
                  }
                </p>
                <p style={{ fontSize: '11px', color: theme.colors.textSecondary, marginTop: '4px' }}>
                  {selectedUnidad === 'kilos' ? 'kg' : 'unidades'}
                </p>
              </div>
            </div>
          </StyledCard>

          {/* Tabla de detalles de movimientos - Ocultar para productos tipo 2 */}
          {productos.find(p => p.id === selectedProducto)?.tipo !== 2 && (
            <StyledCard
              title={
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', flexWrap: 'wrap', gap: '8px' }}>
                  <span style={{ fontSize: '14px', fontWeight: 600 }}>Detalles de Movimientos</span>
                  <div style={{ display: 'flex', gap: '8px', fontSize: '11px', fontWeight: 500, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{
                      padding: '3px 10px',
                      backgroundColor: theme.colors.success,
                      color: 'white',
                      borderRadius: '4px'
                    }}>
                      Ingresos: {new Intl.NumberFormat('es-AR').format(stockData.reduce((sum, item) => sum + (item.cantidad_ingresos || 0), 0))} {selectedUnidad === 'kilos' ? 'kg' : 'unidades'}
                    </span>
                    <span style={{ 
                      padding: '3px 10px', 
                      backgroundColor: theme.colors.error, 
                      color: 'white', 
                      borderRadius: '4px'
                    }}>
                      Egresos: {new Intl.NumberFormat('es-AR').format(stockData.reduce((sum, item) => sum + (item.cantidad_egresos || 0), 0))} {selectedUnidad === 'kilos' ? 'kg' : 'unidades'}
                    </span>
                  </div>
                </div>
              }
              style={{ marginBottom: '16px' }}
              headStyle={{
                padding: '8px 16px',
                fontSize: '14px'
              }}
              bodyStyle={{ padding: '12px' }}
            >
              <Table
                columns={columnasDetalles}
                dataSource={getDetallesMovimientosData}
                rowKey="key"
                pagination={{ 
                  pageSize: 50,
                  showSizeChanger: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} de ${total}`,
                  pageSizeOptions: ['10', '25', '50', '100'],
                  position: ['bottomRight']
                }}
                scroll={{ x: 'max-content', y: 'max(150px, calc(100vh - 500px))' }}
                size="middle"
                onRow={(record) => ({
                  onMouseEnter: (e) => {
                    (e.currentTarget as HTMLElement).style.backgroundColor = '#F5F8FB';
                  },
                  onMouseLeave: (e) => {
                    (e.currentTarget as HTMLElement).style.backgroundColor = record.backgroundColor || '';
                  },
                  style: {
                    backgroundColor: record.backgroundColor || '',
                    transition: 'background-color 0.2s ease'
                  }
                })}
              />
            </StyledCard>
          )}

          {/* Tabla de grupos agrupados */}
          {(stockData.some(item => (item.grupos_ingresos && item.grupos_ingresos.length > 0) || (item.grupos_egresos && item.grupos_egresos.length > 0))) && (
            <StyledCard
              title="Resumen Agrupado"
              style={{ marginBottom: '16px' }}
              headStyle={{
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: 600
              }}
              bodyStyle={{ padding: '12px' }}
            >
              <Table
                columns={columnasResumen}
                dataSource={getResumenAgrupadoData}
                rowKey="key"
                pagination={{ 
                  pageSize: 50,
                  showSizeChanger: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} de ${total}`,
                  pageSizeOptions: ['10', '25', '50', '100'],
                  position: ['bottomRight']
                }}
                scroll={{ x: 'max-content', y: 'max(150px, calc(100vh - 500px))' }}
                size="middle"
                onRow={(record) => ({
                  onMouseEnter: (e) => {
                    (e.currentTarget as HTMLElement).style.backgroundColor = '#F5F8FB';
                  },
                  onMouseLeave: (e) => {
                    (e.currentTarget as HTMLElement).style.backgroundColor = record.backgroundColor || '';
                  },
                  style: {
                    backgroundColor: record.backgroundColor || '',
                    transition: 'background-color 0.2s ease'
                  }
                })}
              />
            </StyledCard>
          )}

        </div>
      ) : (
        <StyledCard darkHeader={false} style={{ marginBottom: '16px' }}>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p style={{ color: theme.colors.textSecondary, fontSize: '14px' }}>
              Seleccione un producto y un cliente para ver la evolución del stock
            </p>
          </div>
        </StyledCard>
      )}
    </div>
  );
};

export default EvolucionStock; 